import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'

import CopyButtonWithLabel from '@/common/CopyButtonWithLabel'
import GradientWrapper from '@/common/GradientWrapper'
import { downloadJson } from '@/helpers'
import ProposalPreview from '@/pages/Multisig/components/ProposalPreview'
import { useSubmitMultisigProposal } from '@/pages/Multisig/CreateProposal/hooks/submit-multisig-proposal'
import { UiButton } from '@/ui/UiButton'
import { UiInput } from '@/ui/UiInput'
import { UiLabel } from '@/ui/UiLabel'

import {
  buildRemoveTransactionMessage,
  RemoveTransactionFormValues,
} from './transactionMessageBuilder'

const removeTransactionSchema = z.object({
  creator: z.string().min(1, 'Creator address is required'),
  depositChainId: z.string().min(1, 'Deposit Chain ID is required'),
  depositTxHash: z.string().min(1, 'Deposit Transaction Hash is required'),
  depositTxIndex: z
    .string()
    .min(1, 'Deposit Transaction Index is required')
    .refine(value => !isNaN(Number(value)) && Number(value) >= 0, {
      message: 'Transaction Index must be a valid non-negative number',
    }),
})

export default function RemoveTransactionForm() {
  const { account } = useParams<{ account: string }>()

  const form = useForm<RemoveTransactionFormValues>({
    resolver: zodResolver(removeTransactionSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      creator: '',
      depositChainId: '',
      depositTxHash: '',
      depositTxIndex: '0',
    },
  })

  const submitProposalTx = useSubmitMultisigProposal({ group: account ?? '' })

  useEffect(() => {
    form.setValue('creator', account ?? '', { shouldValidate: true })
  }, [account, form])

  const watchedValues = useWatch({ control: form.control })

  const messagePayload = useMemo(() => {
    if (!form.formState.isValid || !watchedValues) return null

    try {
      return buildRemoveTransactionMessage(
        watchedValues as RemoveTransactionFormValues,
      )
    } catch {
      return null
    }
  }, [form.formState.isValid, watchedValues])

  const generatedJson = useMemo(() => {
    if (!messagePayload) return null
    return JSON.stringify(messagePayload, null, 2)
  }, [messagePayload])

  const isSubmitting = submitProposalTx.isPending
  const isSubmitDisabled = !form.formState.isValid || isSubmitting

  return (
    <div className='space-y-6'>
      <div className='space-y-2'>
        <h3 className='text-foreground text-lg font-semibold'>
          Remove Transaction Message
        </h3>
        <p className='text-muted-foreground text-sm'>
          Fill in the form to remove a transaction from the system
        </p>
      </div>

      <form
        onSubmit={event => event.preventDefault()}
        className='flex flex-col gap-6'
      >
        <div className='flex flex-col gap-3'>
          <UiLabel htmlFor='creator'>Creator Address</UiLabel>
          <UiInput
            id='creator'
            {...form.register('creator')}
            placeholder='Connect wallet'
            readOnly
            disabled={isSubmitting}
            className='bg-input border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
          />
          {form.formState.errors.creator && (
            <p className='text-destructive text-sm'>
              {form.formState.errors.creator.message}
            </p>
          )}
        </div>

        <div className='border-border space-y-3 rounded-lg border p-3'>
          <h4 className='text-foreground font-medium'>
            Transaction Identification
          </h4>

          <div className='mt-5 flex flex-col gap-3'>
            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='depositChainId'>Deposit Chain ID</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='depositChainId'
                  {...form.register('depositChainId')}
                  placeholder='ethereum'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.depositChainId && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.depositChainId.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='depositTxHash'>
                Deposit Transaction Hash
              </UiLabel>
              <GradientWrapper>
                <UiInput
                  id='depositTxHash'
                  {...form.register('depositTxHash')}
                  placeholder='0x...'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.depositTxHash && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.depositTxHash.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='depositTxIndex'>
                Deposit Transaction Index
              </UiLabel>
              <GradientWrapper>
                <UiInput
                  id='depositTxIndex'
                  {...form.register('depositTxIndex')}
                  placeholder='0'
                  disabled={isSubmitting}
                  type='number'
                  min='0'
                  step='1'
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.depositTxIndex && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.depositTxIndex.message}
                </p>
              )}
            </div>
          </div>
        </div>
      </form>

      {messagePayload && <ProposalPreview messages={messagePayload.messages} />}

      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-wrap items-center gap-2'>
          {generatedJson && (
            <>
              <CopyButtonWithLabel
                value={generatedJson}
                label='Copy JSON'
                copiedLabel='Copied'
                tooltip='Copy JSON'
              />
              <UiButton
                type='button'
                variant='outline'
                size='sm'
                onClick={() =>
                  downloadJson(
                    messagePayload,
                    'remove-transaction-message.json',
                  )
                }
                disabled={isSubmitting}
              >
                Download JSON
              </UiButton>
            </>
          )}
        </div>
        <UiButton
          type='button'
          disabled={isSubmitDisabled}
          onClick={form.handleSubmit(values => {
            const messageObject = buildRemoveTransactionMessage(values)
            submitProposalTx.mutate({
              rawJsonText: JSON.stringify(messageObject, null, 2),
            })
          })}
        >
          {isSubmitting ? 'Submitting…' : 'Submit proposal'}
        </UiButton>
      </div>
    </div>
  )
}
